// Menu Documentação: manual do usuário por perfil, com abas por assunto e busca por palavra-chave.
// Cada perfil vê o seu manual; o admin vê todos, um por aba. Regras em
// ERPApi/docs/funcionalidades/manual-do-usuario.md (RN-MAN-01..04).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, Search, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Card, CardContent } from '@components/Card';
import { usePermissions } from '@hooks/usePermissions';
import { formatDateBR } from '@pages/financial/financialUtils';
import { userGuideService } from '@services/userGuideService';
import { GUIDES, highlightParts, parseGuide, searchTerms, searchTopics, tabOfTopic, type ParsedGuide } from './userGuideUtils';

type GuideState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ok'; guide: ParsedGuide };

const Highlight: React.FC<{ text: string; terms: string[] }> = ({ text, terms }) => (
  <>
    {highlightParts(text, terms).map((p, i) =>
      p.match ? (
        <mark key={i} className="bg-yellow-200 rounded px-0.5">
          {p.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{p.text}</React.Fragment>
      )
    )}
  </>
);

export const UserGuidePage: React.FC = () => {
  const { checkPermission, isLoading: permLoading } = usePermissions();
  const [params, setParams] = useSearchParams();
  const [guides, setGuides] = useState<Record<string, GuideState>>({});
  const [query, setQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const pendingTopic = useRef<string | null>(null);

  const available = useMemo(
    () => (permLoading ? [] : GUIDES.filter((g) => checkPermission(g.permission).hasPermission)),
    [permLoading, checkPermission]
  );

  const profile = available.find((g) => g.profile === params.get('perfil'))?.profile ?? available[0]?.profile;
  const state = profile ? guides[profile] : undefined;
  const guide = state?.status === 'ok' ? state.guide : undefined;
  const tabId = guide?.tabs.find((t) => t.id === params.get('aba'))?.id ?? guide?.tabs[0]?.id;
  const tab = guide?.tabs.find((t) => t.id === tabId);

  useEffect(() => {
    if (!profile || guides[profile]) return;
    setGuides((g) => ({ ...g, [profile]: { status: 'loading' } }));
    userGuideService
      .get(profile)
      .then((res) => setGuides((g) => ({ ...g, [profile]: { status: 'ok', guide: parseGuide(res.html) } })))
      .catch((err: unknown) =>
        setGuides((g) => ({
          ...g,
          [profile]: { status: 'error', message: err instanceof Error ? err.message : 'Falha ao carregar a documentação' },
        }))
      );
  }, [profile, guides]);

  const select = useCallback(
    (next: { perfil?: string; aba?: string }) => {
      const p = new URLSearchParams(params);
      if (next.perfil !== undefined) {
        p.set('perfil', next.perfil);
        p.delete('aba');
      }
      if (next.aba !== undefined) p.set('aba', next.aba);
      setParams(p, { replace: true });
    },
    [params, setParams]
  );

  const goToTopic = useCallback(
    (topicId: string) => {
      if (!guide) return;
      const target = tabOfTopic(guide, topicId);
      if (!target) return;
      pendingTopic.current = topicId;
      setQuery('');
      if (target !== tabId) select({ aba: target });
      else scrollToPending();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guide, tabId, select]
  );

  const scrollToPending = () => {
    const id = pendingTopic.current;
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    pendingTopic.current = null;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('topic-flash');
    window.setTimeout(() => el.classList.remove('topic-flash'), 2000);
  };

  // depois de trocar de aba (ou sair da busca), rola até o tópico pedido
  useEffect(() => {
    scrollToPending();
  });

  // links internos do manual (href="#topico") podem apontar para outra aba
  const onContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a');
    const href = a?.getAttribute('href');
    if (!href?.startsWith('#')) return;
    e.preventDefault();
    goToTopic(href.slice(1));
  };

  const terms = searchTerms(query);
  const hits = guide && terms.length > 0 ? searchTopics(guide, query) : [];
  const searching = terms.length > 0;

  if (permLoading) {
    return <div className="p-6 text-slate-500">Carregando…</div>;
  }
  if (available.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
        Ainda não há documentação para o seu perfil.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-h1 flex items-center gap-2">
            <BookOpen size={26} /> Documentação
          </h1>
          <p className="text-slate-600">
            Como usar o ERP no seu dia a dia: escolha um assunto nas abas ou pesquise por palavra-chave.
            {guide?.updated && <span className="ml-1 text-slate-500">Atualizado em {formatDateBR(guide.updated)}.</span>}
          </p>
        </div>
        <div className="relative w-full lg:w-96">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar (ex.: desconto, nota fiscal, rota)"
            aria-label="Pesquisar na documentação"
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-9 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Limpar pesquisa"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {available.length > 1 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Perfis">
          {available.map((g) => (
            <button
              key={g.profile}
              type="button"
              role="tab"
              aria-selected={g.profile === profile}
              onClick={() => select({ perfil: g.profile })}
              className={clsx(
                'rounded-full px-4 py-1.5 text-sm font-medium border transition-colors',
                g.profile === profile
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              )}
            >
              {g.title}
            </button>
          ))}
        </div>
      )}

      {state?.status === 'loading' && <div className="text-slate-500">Carregando a documentação…</div>}
      {state?.status === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{state.message}</div>
      )}

      {guide && searching && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-600">
              {hits.length === 0
                ? `Nada encontrado para "${query.trim()}". Tente outra palavra ou menos palavras.`
                : `${hits.length} ${hits.length === 1 ? 'resultado' : 'resultados'} para "${query.trim()}"`}
            </p>
            <ul className="divide-y divide-slate-100">
              {hits.map((h) => (
                <li key={h.topicId}>
                  <button
                    type="button"
                    onClick={() => goToTopic(h.topicId)}
                    className="w-full text-left py-3 px-2 rounded hover:bg-slate-50"
                  >
                    <span className="text-xs font-medium uppercase tracking-wide text-blue-600">{h.tabTitle}</span>
                    <span className="block font-semibold text-slate-900">
                      <Highlight text={h.title} terms={terms} />
                    </span>
                    <span className="block text-sm text-slate-600">
                      <Highlight text={h.snippet} terms={terms} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {guide && !searching && tab && (
        <>
          <div className="border-b border-slate-200 overflow-x-auto">
            <nav className="flex min-w-max" aria-label="Assuntos" role="tablist">
              {guide.tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={t.id === tabId}
                  onClick={() => select({ aba: t.id })}
                  className={clsx(
                    'py-3 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    t.id === tabId
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                  )}
                >
                  {t.title}
                </button>
              ))}
            </nav>
          </div>

          <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
            <aside className="hidden lg:block">
              <div className="sticky top-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Nesta aba</p>
                {tab.topics.map((tp) => (
                  <button
                    key={tp.id}
                    type="button"
                    onClick={() => goToTopic(tp.id)}
                    className="block w-full text-left text-sm text-slate-600 hover:text-blue-600 py-1"
                  >
                    {tp.title}
                  </button>
                ))}
              </div>
            </aside>
            <Card>
              <CardContent className="p-4 sm:p-6">
                {/* Conteúdo do próprio repositório (ERPApi/internal/userguide), validado no back-end: sem script, estilo nem link externo. */}
                <div
                  ref={contentRef}
                  className="user-guide-content"
                  onClick={onContentClick}
                  dangerouslySetInnerHTML={{ __html: tab.html }}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default UserGuidePage;
